"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { setLocaleAction } from "@/lib/i18n/actions";
import type { Language, LocaleCode } from "@/lib/i18n/locales";

type NavItem = { label: string; href: string };

function Logo() {
  return (
    <div className="flex items-center gap-2">
      <div className="relative h-9 w-9 rounded-xl bg-gradient-to-br from-[#1E9FF2] via-[#0C56DB] to-[#0D102F] grid place-items-center shadow-md">
        <span className="text-white font-bold text-base tracking-tight">K</span>
        <span className="absolute -right-0.5 -bottom-0.5 h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-white" />
      </div>
      <span className="text-xl font-bold tracking-tight text-slate-900">Kirshi</span>
    </div>
  );
}

function LanguageSwitcher({ locale, languages }: { locale: LocaleCode; languages: Language[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = languages.find((l) => l.code === locale) ?? languages[0];

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  if (!current || languages.length < 2) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3M3 12h18" />
        </svg>
        <span>{current.name}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" strokeWidth={2.2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div role="listbox" className="absolute right-0 mt-2 w-48 rounded-xl bg-white shadow-xl ring-1 ring-slate-100 p-1 z-50">
          {languages.map((l) => (
            <form key={l.code} action={setLocaleAction}>
              <input type="hidden" name="locale" value={l.code} />
              <button
                type="submit"
                role="option"
                aria-selected={l.code === locale}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${
                  l.code === locale ? "bg-[#0C56DB]/5 text-[#0C56DB] font-semibold" : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span>{l.name}</span>
                <span className="text-xs text-slate-400 uppercase">{l.code}</span>
              </button>
            </form>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PublicNav({
  locale,
  languages,
  navLinks,
  loginLabel,
  ctaLabel,
}: {
  locale: LocaleCode;
  languages: Language[];
  navLinks: NavItem[];
  loginLabel: string;
  ctaLabel: string;
}) {
  const pathname = usePathname();

  return (
    <header className="sticky top-0 z-40 border-b border-slate-100/80 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        <Link href="/" aria-label="Kirshi home"><Logo /></Link>
        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => {
            const active = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                className={`relative text-sm font-medium transition-colors ${active ? "text-[#0C56DB]" : "text-slate-600 hover:text-slate-900"}`}
              >
                {link.label}
                {active && <span className="absolute -bottom-[21px] left-0 right-0 h-0.5 rounded-full bg-[#0C56DB]" />}
              </Link>
            );
          })}
        </nav>
        <div className="flex items-center gap-1">
          <LanguageSwitcher locale={locale} languages={languages} />
          <Link href="/login" className="hidden sm:inline-flex text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2">{loginLabel}</Link>
          <Link href="/login" className="inline-flex items-center gap-1.5 rounded-xl bg-[#0C56DB] hover:bg-[#0a48b8] px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_20px_-8px_rgba(12,86,219,0.7)] transition-all">
            {ctaLabel}
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.4} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </div>
    </header>
  );
}
